<template>
  <div class="page-content">
    <h1 class="pb-1">Dashboard</h1>

    <!-- Error Message -->
    <v-alert v-if="error" type="error" dismissible @update:modelValue="error = null">
      {{ error }}
    </v-alert>

    <!-- Tabs -->
    <v-card class="mx-auto elevation-2 tabs-card">
      <v-container fluid>
        <v-row align="center" justify="space-between">
          <v-tabs v-model="activeTab" class="dashboard-tab" show-arrows next-icon="mdi-arrow-right-bold-box-outline" prev-icon="mdi-arrow-left-bold-box-outline">
            <v-tab value="studio">Studio</v-tab>
            <v-tab v-for="tab in tabs" :key="tab.value" :value="tab.value">{{ tab.label }}</v-tab>
          </v-tabs>
          <v-text-field
            v-model="modelFilter"
            label="Filter by Name"
            outlined
            density="compact"
            hide-details
            rounded
            prepend-inner-icon="mdi-account-search"
            style="max-width: 230px; margin-right: 12px;"
            @input="filterModels"
          ></v-text-field>
        </v-row>
      </v-container>
    </v-card>

    <!-- Charts Row -->
    <v-row class="mt-3">
      <v-col cols="12" md="3">
        <v-card class="elevation-0.5 pa-2 compact-card">
          <v-card-title style="font-size: 14px; text-align: center; padding: 8px 0;">Daily Goal ${{ salesTarget.toFixed(2) }}</v-card-title>
          <v-card-text style="padding: 0;">
            <DonutChart :total-sales="totalSales" :sales-target="salesTarget" height="150" />
          </v-card-text>
        </v-card>
      </v-col>
      <v-col cols="12" md="6">
        <v-card class="elevation-0.5 pa-2 compact-card">
          <v-card-title style="font-size: 14px; text-align: center; padding: 8px 0;">Daily Sales Over Time</v-card-title>
          <v-card-text style="padding: 0;">
            <Barchart :daily-sales="chartDailySales" height="150" />
          </v-card-text>
        </v-card>
      </v-col>
      <v-col cols="12" md="3">
        <v-card class="elevation-0.5 pa-2 compact-card">
          <v-card-title style="font-size: 14px; text-align: center; padding: 8px 0;">Sales by Platform</v-card-title>
          <v-card-text style="padding: 0;">
            <DonutChartWebsites :platform-sales="filteredPlatformSales" height="150" />
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>

    <!-- Cards Row: Input Cards -->
    <v-row style="margin-top: 10px;">
      <!-- 1st Card: Daily Sales -->
      <v-col cols="12" md="3">
        <v-card :class="['elevation-2', activeCard === 'dailySales' ? 'active-card' : '']" @click="setActiveCard('dailySales')" style="min-height: 180px; border-radius: 6px;">
          <v-card-text style="padding: 8px 16px; text-align: center;">
            <div v-if="dailySalesBreakdown.total > 0" class="headline d-flex justify-center align-center">
              $<span class="text-h6">{{ dailySalesBreakdown.total.toFixed(2) }}</span>
            </div>
            <div v-else style="font-size: 12px; color: #666;">No sales data for {{ formatDate(dailySalesDate) }}</div>
          </v-card-text>
          <v-card-title style="position: relative; display: flex; align-items: center; padding: 8px 16px;">
            <v-icon style="margin-right: 8px; font-size: 28px;">mdi-calendar</v-icon>
            <span style="position: absolute; left: 50%; transform: translateX(-50%); font-size: 16px; font-weight: 600;">Daily Sales</span>
          </v-card-title>
          <v-card-text style="padding: 4px 16px;">
            <v-date-input
              ref="dailyDatePicker"
              v-model="dailySalesDate"
              label="Select Date"
              prepend-icon=""
              hide-actions="false"
              placeholder="yyyy/mm/dd"
              clearable="false"
              @update:modelValue="fetchDailySalesAndModel"
            ></v-date-input>
          </v-card-text>
        </v-card>
      </v-col>

      <!-- 2nd Card: Select Date Range -->
      <v-col cols="12" md="3">
        <v-card :class="['elevation-2', activeCard === 'dateRange' ? 'active-card' : '']" @click="setActiveCard('dateRange')" style="min-height: 180px; border-radius: 6px;">
          <v-card-text style="padding: 8px 16px; text-align: center;">
            <div v-if="rangeSales !== null && rangeSales > 0" class="headline d-flex justify-center align-center">
              $<span class="text-h6">{{ rangeSales.toFixed(2) }}</span>
            </div>
            <div v-else style="font-size: 12px; color: #666;">Select a date range to view sales</div>
          </v-card-text>
          <v-card-title style="position: relative; display: flex; align-items: center; padding: 8px 16px;">
            <v-icon style="margin-right: 8px; font-size: 28px;">mdi-calendar-multiple</v-icon>
            <span style="position: absolute; left: 50%; transform: translateX(-50%); font-size: 16px; font-weight: 600;">Select Date Range</span>
          </v-card-title>
          <v-card-text style="padding: 4px 16px;">
            <v-date-input
              v-model="dateSelectRange"
              label="Select Range"
              prepend-icon=""
              max-width="368"
              multiple="range"
              @update:modelValue="handleDateRangeSelection"
              ref="datePicker"
              clearable="false"
            />
          </v-card-text>
        </v-card>
      </v-col>

      <!-- 3rd Card: Select Period -->
      <v-col cols="12" md="3">
        <v-card :class="['elevation-2', activeCard === 'selectPeriod' ? 'active-card' : '']" @click="setActiveCard('selectPeriod')" style="min-height: 180px; border-radius: 6px;">
          <v-card-text style="padding: 8px 16px; text-align: center;">
            <div v-if="periodSales !== null && periodSales > 0" class="headline d-flex justify-center align-center">
              $<span class="text-h6">{{ periodSales.toFixed(2) }}</span>
            </div>
            <div v-else style="font-size: 12px; color: #666;">Select a period to view sales</div>
          </v-card-text>
          <v-card-title style="position: relative; display: flex; align-items: center; padding: 8px 16px;">
            <v-icon style="margin-right: 8px; font-size: 28px;">mdi-calendar-multiple-check</v-icon>
            <span style="position: absolute; left: 50%; transform: translateX(-50%); font-size: 16px; font-weight: 600;">Select Period</span>
          </v-card-title>
          <v-card-text style="padding: 4px 16px;">
            <v-select
              v-model="selectedPeriod"
              :items="periodOptions"
              item-title="text"
              item-value="value"
              label="Select Period"
              outlined
              :disabled="loadingPeriods || periodOptions.length === 0 || periodOptions[0]?.value === null"
              @update:modelValue="fetchPeriodSales"
            ></v-select>
          </v-card-text>
        </v-card>
      </v-col>

      <!-- 4th Card: View Sites -->
      <v-col cols="12" md="3">
        <v-card :class="['elevation-2', activeCard === 'viewSites' ? 'active-card' : '']" @click="setActiveCard('viewSites')" style="min-height: 180px; border-radius: 6px;">
          <v-card-title style="position: relative; display: flex; align-items: center; padding: 8px 16px;">
            <v-icon style="margin-right: 8px; font-size: 28px;">mdi-web</v-icon>
            <span style="position: absolute; left: 50%; transform: translateX(-50%); font-size: 16px; font-weight: 600;">View Sites</span>
          </v-card-title>
          <v-card-text style="padding: 4px 16px;">
            <v-row dense>
              <v-col v-for="site in sites" :key="site.name" cols="6">
                <v-chip
                  :color="site.enabled ? siteColors[site.name] : 'grey'"
                  text-color="white"
                  small
                  style="width: 100%; justify-content: center; cursor: pointer;"
                  @click="toggleSite(site)"
                >
                  {{ site.name }}
                </v-chip>
              </v-col>
            </v-row>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>

    <!-- Cards Row: Top Performers -->
    <v-row style="margin-top: 10px;">
      <v-col cols="12" md="3">
        <v-card class="elevation-2" style="min-height: 90px; border-radius: 6px;">
          <v-card-title style="position: relative; display: flex; align-items: center; padding: 8px 16px;">
            <v-icon style="margin-right: 8px; font-size: 20px;">mdi-crown</v-icon>
            <span style="position: absolute; left: 50%; transform: translateX(-50%); font-size: 14px; font-weight: 600;">Model of the Day</span>
          </v-card-title>
          <v-card-text style="padding: 4px 16px; text-align: center;">
            <span v-if="modelOfTheDay" style="font-size: 16px; font-weight: 500;">{{ modelOfTheDay.stageName || modelOfTheDay.fullName || 'Unknown' }}</span>
            <span v-else style="font-size: 12px; color: #666;">No data for {{ formatDate(dailySalesDate) }}</span>
          </v-card-text>
        </v-card>
      </v-col>
      <v-col cols="12" md="3">
        <v-card class="elevation-2" style="min-height: 90px; border-radius: 6px;">
          <v-card-title style="position: relative; display: flex; align-items: center; padding: 8px 16px;">
            <v-icon style="margin-right: 8px; font-size: 20px;">mdi-crown</v-icon>
            <span style="position: absolute; left: 50%; transform: translateX(-50%); font-size: 14px; font-weight: 600;">Top Model (Range)</span>
          </v-card-title>
          <v-card-text style="padding: 4px 16px; text-align: center;">
            <span v-if="topModelRange" style="font-size: 16px; font-weight: 500;">{{ topModelRange.stageName || topModelRange.fullName || 'Unknown' }}</span>
            <span v-else style="font-size: 12px; color: #666;">No data for range</span>
          </v-card-text>
        </v-card>
      </v-col>
      <v-col cols="12" md="3">
        <v-card class="elevation-2" style="min-height: 90px; border-radius: 6px;">
          <v-card-title style="position: relative; display: flex; align-items: center; padding: 8px 16px;">
            <v-icon style="margin-right: 8px; font-size: 20px;">mdi-crown</v-icon>
            <span style="position: absolute; left: 50%; transform: translateX(-50%); font-size: 14px; font-weight: 600;">Model of the Period</span>
          </v-card-title>
          <v-card-text style="padding: 4px 16px; text-align: center;">
            <span v-if="modelOfThePeriod" style="font-size: 16px; font-weight: 500;">{{ modelOfThePeriod.stageName || modelOfThePeriod.fullName || 'Unknown' }}</span>
            <span v-else style="font-size: 12px; color: #666;">No data for period</span>
          </v-card-text>
        </v-card>
      </v-col>
      <v-col cols="12" md="3">
        <v-card class="elevation-2" style="min-height: 90px; border-radius: 6px;">
          <v-card-title style="position: relative; display: flex; align-items: center; padding: 8px 16px;">
            <v-icon style="margin-right: 8px; font-size: 20px;">mdi-web</v-icon>
            <span style="position: absolute; left: 50%; transform: translateX(-50%); font-size: 14px; font-weight: 600;">
              {{ activeCard === 'dailySales' ? 'Top Site (Daily)' : activeCard === 'dateRange' ? 'Top Site (Range)' : activeCard === 'selectPeriod' ? 'Top Site (Period)' : 'Top Site' }}
            </span>
          </v-card-title>
          <v-card-text style="padding: 4px 16px; text-align: center;">
            <span v-if="topSite" style="font-size: 16px; font-weight: 500;">{{ topSite.name }}</span>
            <span v-else style="font-size: 12px; color: #666;">No site data</span>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>
  </div>
</template>





<script>
import secureAxios from '@/utils/secureAxios';
import Barchart from '@/components/BarChart.vue';
import DonutChartWebsites from '@/components/DonutChartWebsites.vue';
import DonutChart from '@/components/DonutChart.vue';

export default {
  name: 'Dashboard',
  components: {
    Barchart,
    DonutChartWebsites,
    DonutChart
  },
  data() {
    const yesterday = new Date();
    yesterday.setDate(yesterday.getDate() - 1); // Yesterday: April 6, 2025
    const firstOfMonth = new Date(yesterday);
    firstOfMonth.setDate(1); // First of current month: April 1, 2025

    return {
      activeTab: 'studio',
      activeCard: 'dailySales', // Default to dailySales
      tabs: [
        { value: 'morning', label: 'Morning' },
        { value: 'evening', label: 'Evening' },
        { value: 'night', label: 'Night' },
        { value: 'satellite', label: 'Satellite' },
        { value: 'lata', label: 'Lata' },
        { value: 'active', label: 'Active' },
      ],
      modelFilter: '',
      selectedModel: null,
      dailySalesDate: yesterday,
      dateSelectRange: [firstOfMonth, yesterday],
      periodOptions: [],
      selectedPeriod: null,
      models: [],
      filteredModels: [],
      modelOfTheDay: null,
      topModelRange: null,
      modelOfThePeriod: null,
      topSite: null,
      sites: [
        { name: 'Flirt4Free', enabled: true },
        { name: 'Streamate', enabled: true },
        { name: 'LiveJasmin', enabled: true },
        { name: 'Chaturbate', enabled: true },
        { name: 'Stripchat', enabled: true },
        { name: 'Xlove', enabled: true }
      ],
      siteColors: {
        'Flirt4Free': 'rgba(2, 168, 224, 0.5)',
        'Streamate': 'rgba(35, 101, 159, 0.5)',
        'LiveJasmin': 'rgba(188, 0, 2, 0.5)',
        'Chaturbate': 'rgba(255, 203, 74, 0.5)',
        'Stripchat': 'rgba(119, 2, 2, 0.5)',
        'Xlove': 'rgba(0, 128, 0, 0.5)'
      },
      dailySales: null,
      dailySalesBreakdown: {
        flirt4free: 0,
        streamate: 0,
        livejasmin: 0,
        chaturbate: 0,
        stripchat: 0,
        xlove: 0,
        total: 0
      },
      rangeSales: null,
      periodSales: null,
      chartDailySales: [],
      platformSales: {
        flirt4free: 0,
        streamate: 0,
        livejasmin: 0,
        chaturbate: 0,
        stripchat: 0,
        xlove: 0
      },
      totalSales: 0,
      salesTarget: 1000,
      loadingPeriods: false,
      error: null // Add error state
    };
  },
  computed: {
    selectedPeriodText() {
      const period = this.periodOptions.find(p => p.value === this.selectedPeriod);
      return period ? period.text : '';
    },
    filteredPlatformSales() {
      return {
        flirt4free: this.sites.find(s => s.name === 'Flirt4Free').enabled ? this.platformSales.flirt4free : 0,
        streamate: this.sites.find(s => s.name === 'Streamate').enabled ? this.platformSales.streamate : 0,
        livejasmin: this.sites.find(s => s.name === 'LiveJasmin').enabled ? this.platformSales.livejasmin : 0,
        chaturbate: this.sites.find(s => s.name === 'Chaturbate').enabled ? this.platformSales.chaturbate : 0,
        stripchat: this.sites.find(s => s.name === 'Stripchat').enabled ? this.platformSales.stripchat : 0,
        xlove: this.sites.find(s => s.name === 'Xlove').enabled ? this.platformSales.xlove : 0
      };
    }
  },
  watch: {
    activeTab() {
      this.fetchAllData();
    },
    modelFilter() {
      this.filterModels();
      this.fetchAllData();
    },
    dailySalesDate(newVal) {
      if (!newVal || !(newVal instanceof Date) || isNaN(newVal.getTime())) {
        const yesterday = new Date();
        yesterday.setDate(yesterday.getDate() - 1);
        this.dailySalesDate = yesterday;
        console.log('Invalid dailySalesDate, reset to yesterday:', this.formatDate(yesterday));
      }
      console.log('dailySalesDate updated to:', this.formatDate(newVal));
      this.fetchDailySalesAndModel(newVal);
    },
    dateSelectRange(newRange) {
      if (newRange.length >= 2) {
        console.log("📅 New date range selected:", newRange);
        this.fetchRangeSales();
        this.fetchTopModelRange();
        if (this.activeCard === 'dateRange') this.fetchTopSite();
      }
    },
    selectedPeriod() {
      this.fetchModelOfThePeriod();
      this.fetchPeriodSales();
      if (this.activeCard === 'selectPeriod') this.fetchTopSite();
    },
    activeCard(newCard) {
      console.log('Active card changed to:', newCard);
      this.fetchTopSite();
    }
  },
  async mounted() {
    console.log('Mounted with initial dailySalesDate:', this.formatDate(this.dailySalesDate));
    await this.initializeDashboard();
  },
  methods: {
    async initializeDashboard() {
      const yesterday = new Date();
      yesterday.setDate(yesterday.getDate() - 1);
      this.dailySalesDate = yesterday; // Set to yesterday
      console.log('Initialized dailySalesDate to:', this.formatDate(this.dailySalesDate));

      // Simulate calendar selection
      this.$nextTick(async () => {
        if (this.$refs.dailyDatePicker) {
          console.log('Simulating calendar selection for:', this.formatDate(this.dailySalesDate));
          this.dailySalesDate = new Date(yesterday); // Reassign to trigger reactivity
          await this.fetchDailySalesAndModel(this.dailySalesDate);
        }
      });

      await this.fetchPeriods();
      await this.fetchAllData();
    },
    async fetchAllData() {
      await Promise.all([
        this.fetchModels(),
        this.fetchRangeSales(),
        this.fetchPeriodSales(),
        this.fetchTopModelRange(),
        this.fetchModelOfThePeriod(),
        this.fetchTopSite()
      ]);
    },
    async fetchDailySalesAndModel(date) {
      const dateStr = this.formatDate(date);
      console.log('Fetching daily sales and model for:', dateStr);
      await Promise.all([
        this.fetchDailySales(dateStr),
        this.fetchModelOfTheDay(dateStr)
      ]);
    },
    handleError(error, context) {
      console.error(`Error in ${context}:`, error);
      if (error.response) {
        this.error = `Error in ${context}: ${error.response.data.message || 'Failed to load data'}`;
      } else if (error.request) {
        this.error = `Network error in ${context}: Could not reach the server.`;
      } else {
        this.error = `Error in ${context}: ${error.message || 'Something broke!'}`;
      }
    },
    async fetchPeriods() {
      this.loadingPeriods = true;
      try {
        const response = await secureAxios.get('/api/dashboard/periods');
        this.periodOptions = response.data.map(period => ({
          value: period.id,
          text: `${period.name} (${new Date(period.start_date).toLocaleDateString()} - ${new Date(period.payment_date).toLocaleDateString()})`,
          start_date: period.start_date,
          payment_date: period.payment_date
        })) || [];

        this.periodOptions.sort((a, b) => new Date(a.payment_date) - new Date(b.payment_date));

        const today = new Date();
        let currentPeriodIndex = this.periodOptions.findIndex(period => {
          const paymentDate = new Date(period.payment_date);
          return today <= paymentDate;
        });

        if (currentPeriodIndex === -1) {
          currentPeriodIndex = this.periodOptions.length - 1;
        }

        const currentPeriod = this.periodOptions[currentPeriodIndex];
        this.selectedPeriod = currentPeriod ? currentPeriod.value : null;

        if (this.selectedPeriod) {
          console.log(`Selected Period: ${currentPeriod.text}, ID: ${this.selectedPeriod}`);
          await this.fetchPeriodSales();
          await this.fetchModelOfThePeriod();
        } else {
          console.warn('No valid period selected.');
        }
      } catch (error) {
        this.handleError(error, 'fetchPeriods');
        this.periodOptions = [{ value: null, text: 'Error Loading Periods' }];
        this.selectedPeriod = null;
      } finally {
        this.loadingPeriods = false;
      }
    },
    async fetchModels() {
      try {
        const params = {};
        if (this.activeTab !== 'studio') {
          params.location = this.activeTab;
        }
        if (this.activeTab === 'active') {
          params.status = 'Active';
        }
        const response = await secureAxios.get('/api/dashboard/models', { params });
        this.models = response.data || [];
        this.filterModels();
      } catch (error) {
        this.handleError(error, 'fetchModels');
        this.models = [];
        this.filteredModels = [];
      }
    },
    filterModels() {
      if (!this.modelFilter) {
        this.filteredModels = this.models;
        this.selectedModel = null;
      } else {
        this.filteredModels = this.models.filter(model =>
          (model.fullName?.toLowerCase().includes(this.modelFilter.toLowerCase()) ||
          model.stageName?.toLowerCase().includes(this.modelFilter.toLowerCase()))
        );
        this.selectedModel = this.filteredModels.length > 0 ? this.filteredModels[0] : null;
      }
    },
    async fetchModelOfTheDay(dateStr) {
      if (!dateStr) return;
      console.log('Fetching Model of the Day for:', dateStr);
      try {
        const params = {
          date: dateStr,
          location: this.activeTab === 'studio' ? null : this.activeTab,
          sites: this.sites.filter(s => s.enabled).map(s => s.name.toLowerCase())
        };
        if (this.selectedModel) {
          params.model_id = this.selectedModel.id;
        }
        if (this.activeTab === 'active') {
          params.status = 'Active';
        }
        const response = await secureAxios.get('/api/dashboard/top-model-day', { params });
        this.modelOfTheDay = response.data || null;
        console.log('Model of the Day response:', response.data);
      } catch (error) {
        this.handleError(error, 'fetchModelOfTheDay');
        this.modelOfTheDay = null;
      }
    },
    async fetchTopModelRange() {
      if (!this.dateSelectRange || this.dateSelectRange.length < 2) return;
      const dateArray = [...this.dateSelectRange]
        .map(d => new Date(d))
        .sort((a, b) => a - b);
      const startDate = this.formatDate(dateArray[0]);
      const endDate = this.formatDate(dateArray[dateArray.length - 1]);
      try {
        const params = {
          startDate,
          endDate,
          location: this.activeTab === 'studio' ? null : this.activeTab,
          sites: this.sites.filter(s => s.enabled).map(s => s.name.toLowerCase())
        };
        if (this.selectedModel) {
          params.model_id = this.selectedModel.id;
        }
        if (this.activeTab === 'active') {
          params.status = 'Active';
        }
        const response = await secureAxios.get('/api/dashboard/top-model-range', { params });
        this.topModelRange = response.data || null;
        console.log('Top Model (Range):', this.topModelRange);
      } catch (error) {
        this.handleError(error, 'fetchTopModelRange');
        this.topModelRange = null;
      }
    },
    async fetchModelOfThePeriod() {
      if (!this.selectedPeriod) return;
      try {
        const params = {
          periodId: this.selectedPeriod,
          location: this.activeTab === 'studio' ? null : this.activeTab,
          sites: this.sites.filter(s => s.enabled).map(s => s.name.toLowerCase())
        };
        if (this.selectedModel) {
          params.model_id = this.selectedModel.id;
        }
        if (this.activeTab === 'active') {
          params.status = 'Active';
        }
        const response = await secureAxios.get('/api/dashboard/top-model-period', { params });
        this.modelOfThePeriod = response.data || null;
        console.log('Model of the Period:', this.modelOfThePeriod);
      } catch (error) {
        this.handleError(error, 'fetchModelOfThePeriod');
        this.modelOfThePeriod = null;
      }
    },
    async fetchTopSite() {
      try {
        const params = {
          location: this.activeTab === 'studio' ? null : this.activeTab,
          sites: this.sites.filter(s => s.enabled).map(s => s.name.toLowerCase())
        };
        if (this.selectedModel) {
          params.model_id = this.selectedModel.id;
        }
        if (this.activeTab === 'active') {
          params.status = 'Active';
        }

        let endpoint;
        if (this.activeCard === 'dailySales' && this.dailySalesDate) {
          const dateStr = this.formatDate(this.dailySalesDate);
          endpoint = `/api/dashboard/top-site-day/${dateStr}`;
        } else if (this.activeCard === 'dateRange' && this.dateSelectRange.length >= 2) {
          const dateArray = [...this.dateSelectRange].map(d => new Date(d)).sort((a, b) => a - b);
          const startDate = this.formatDate(dateArray[0]);
          const endDate = this.formatDate(dateArray[dateArray.length - 1]);
          endpoint = `/api/dashboard/top-site-range/${startDate}/${endDate}`;
        } else if (this.activeCard === 'selectPeriod' && this.selectedPeriod) {
          endpoint = `/api/dashboard/top-site-period/${this.selectedPeriod}`;
        } else {
          console.log('No active card selected, skipping top site fetch');
          return;
        }

        console.log(`Fetching top site from: ${endpoint} with params:`, params);
        const response = await secureAxios.get(endpoint, { params });
        this.topSite = response.data || null;
        console.log(`Top Site (${this.activeCard || 'none'}):`, this.topSite);
      } catch (error) {
        this.handleError(error, 'fetchTopSite');
        this.topSite = null;
      }
    },
    formatDate(date) {
      if (!date || !(date instanceof Date) || isNaN(date.getTime())) {
        const localYesterday = new Date();
        localYesterday.setDate(localYesterday.getDate() - 1);
        return localYesterday.toISOString().split('T')[0];
      }
      return date.toISOString().split('T')[0];
    },
    updateChartData(salesData, dailyBreakdown = null) {
      this.platformSales = {
        flirt4free: Number(salesData.flirt4free) || 0,
        streamate: Number(salesData.streamate) || 0,
        livejasmin: Number(salesData.livejasmin) || 0,
        chaturbate: Number(salesData.chaturbate) || 0,
        stripchat: Number(salesData.stripchat) || 0,
        xlove: Number(salesData.xlove) || 0
      };
      this.totalSales = Number(salesData.total) || 0;

      if (dailyBreakdown) {
        this.chartDailySales = dailyBreakdown.map(row => ({
          date: row.date,
          total: Number(row.total) || 0
        }));
      } else {
        this.chartDailySales = [{
          date: this.formatDate(this.dailySalesDate),
          total: this.totalSales
        }];
      }
    },
    async fetchDailySales(dateStr) {
      if (!dateStr) {
        const yesterday = new Date();
        yesterday.setDate(yesterday.getDate() - 1);
        dateStr = this.formatDate(yesterday);
        this.dailySalesDate = yesterday;
        console.warn('dateStr was null, set to yesterday:', dateStr);
      }
      console.log('Fetching daily sales for date:', dateStr);
      try {
        const params = {
          location: this.activeTab === 'studio' ? null : this.activeTab,
          sites: this.sites.filter(s => s.enabled).map(s => s.name.toLowerCase())
        };
        if (this.selectedModel) {
          params.model_id = this.selectedModel.id;
        }
        if (this.activeTab === 'active') {
          params.status = 'Active';
        }
        const response = await secureAxios.get(`/api/dashboard/daily-sales/${dateStr}`, { params });

        this.dailySales = Number(response.data.total) || 0;
        this.dailySalesBreakdown = {
          flirt4free: Number(response.data.flirt4free) || 0,
          streamate: Number(response.data.streamate) || 0,
          livejasmin: Number(response.data.livejasmin) || 0,
          chaturbate: Number(response.data.chaturbate) || 0,
          stripchat: Number(response.data.stripchat) || 0,
          xlove: Number(response.data.xlove) || 0,
          total: Number(response.data.total) || 0
        };

        this.updateChartData(this.dailySalesBreakdown);
        console.log(`Daily Sales for ${dateStr}:`, this.dailySalesBreakdown);
      } catch (error) {
        this.handleError(error, 'fetchDailySales');
        this.dailySales = 0;
        this.dailySalesBreakdown = {
          flirt4free: 0,
          streamate: 0,
          livejasmin: 0,
          chaturbate: 0,
          stripchat: 0,
          xlove: 0,
          total: 0
        };
        this.updateChartData(this.dailySalesBreakdown);
      }
    },
    async fetchRangeSales() {
      if (!this.dateSelectRange || this.dateSelectRange.length < 2) {
        console.warn('Invalid date range');
        return;
      }
      const dateArray = [...this.dateSelectRange]
        .map(d => new Date(d))
        .sort((a, b) => a - b);
      const startDate = this.formatDate(dateArray[0]);
      const endDate = this.formatDate(dateArray[dateArray.length - 1]);
      try {
        const params = {
          location: this.activeTab === 'studio' ? null : this.activeTab,
          sites: this.sites.filter(s => s.enabled).map(s => s.name.toLowerCase())
        };
        if (this.selectedModel) {
          params.model_id = this.selectedModel.id;
        }
        if (this.activeTab === 'active') {
          params.status = 'Active';
        }
        const totalResponse = await secureAxios.get(`/api/dashboard/range-sales/${startDate}/${endDate}`, { params });
        const breakdownResponse = await secureAxios.get(`/api/dashboard/range-sales-breakdown/${startDate}/${endDate}`, { params });

        this.rangeSales = Number(totalResponse.data.total) || 0;

        const rangeBreakdown = {
          flirt4free: Number(totalResponse.data.flirt4free) || 0,
          streamate: Number(totalResponse.data.streamate) || 0,
          livejasmin: Number(totalResponse.data.livejasmin) || 0,
          chaturbate: Number(totalResponse.data.chaturbate) || 0,
          stripchat: Number(totalResponse.data.stripchat) || 0,
          xlove: Number(totalResponse.data.xlove) || 0,
          total: this.rangeSales
        };

        this.updateChartData(rangeBreakdown, breakdownResponse.data);
        console.log(`Range Sales (${startDate} to ${endDate}):`, rangeBreakdown);
      } catch (error) {
        this.handleError(error, 'fetchRangeSales');
        this.rangeSales = 0;
        this.updateChartData({
          flirt4free: 0,
          streamate: 0,
          livejasmin: 0,
          chaturbate: 0,
          stripchat: 0,
          xlove: 0,
          total: 0
        });
      }
    },
    async fetchPeriodSales() {
      if (!this.selectedPeriod) {
        console.warn('No period selected');
        return;
      }
      try {
        const params = {
          location: this.activeTab === 'studio' ? null : this.activeTab,
          sites: this.sites.filter(s => s.enabled).map(s => s.name.toLowerCase())
        };
        if (this.selectedModel) {
          params.model_id = this.selectedModel.id;
        }
        if (this.activeTab === 'active') {
          params.status = 'Active';
        }
        const totalResponse = await secureAxios.get(`/api/dashboard/period-sales/${this.selectedPeriod}`, { params });
        const breakdownResponse = await secureAxios.get(`/api/dashboard/period-sales-breakdown/${this.selectedPeriod}`, { params });

        this.periodSales = Number(totalResponse.data.total) || 0;

        const periodBreakdown = {
          flirt4free: Number(totalResponse.data.flirt4free) || 0,
          streamate: Number(totalResponse.data.streamate) || 0,
          livejasmin: Number(totalResponse.data.livejasmin) || 0,
          chaturbate: Number(totalResponse.data.chaturbate) || 0,
          stripchat: Number(totalResponse.data.stripchat) || 0,
          xlove: Number(totalResponse.data.xlove) || 0,
          total: this.periodSales
        };

        this.updateChartData(periodBreakdown, breakdownResponse.data);
      } catch (error) {
        this.handleError(error, 'fetchPeriodSales');
        this.periodSales = 0;
        this.updateChartData({
          flirt4free: 0,
          streamate: 0,
          livejasmin: 0,
          chaturbate: 0,
          stripchat: 0,
          xlove: 0,
          total: 0
        });
      }
    },
    setActiveCard(card) {
      this.activeCard = card;
    },
    handleDateRangeSelection(newRange) {
      if (newRange.length === 2) {
        this.$nextTick(() => {
          if (this.$refs.datePicker) {
            this.$refs.datePicker.isActive = false;
          }
        });
      }
    },
    toggleSite(site) {
      site.enabled = !site.enabled;
      this.fetchAllData();
      this.fetchDailySalesAndModel(this.dailySalesDate);
    }
  }
};
</script>

<style scoped>
.dashboard-tab .v-tab {
  background-color: white;
  color: #2C74B3;
}
.active-card {
  border: 2px solid #1976d2 !important;
}
</style>